import React from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { SEOHead } from '../hooks/useSEO';

const NotFound: React.FC = () => {
  return (
    <>
      <SEOHead
        title="Page Not Found | TyrantShield Technologies Wyoming"
        description="The page you're looking for doesn't exist. Explore our IT services, cybersecurity solutions, and technology consulting for Wyoming businesses."
        canonical="https://www.tyrantshield.com/404"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* 404 Error */}
          <div className="mb-8">
            <h1 className="text-9xl font-bold text-blue-600 mb-4">404</h1>
            <h2 className="text-3xl font-heading font-bold text-slate-900 mb-4">
              Page Not Found
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Sorry, we couldn't find the page you're looking for. The page may have been moved, deleted, or you may have entered an incorrect URL.
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Link
              to="/"
              className="flex flex-col items-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow group"
            >
              <HomeIcon className="w-12 h-12 text-blue-600 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-slate-900 mb-2">Go Home</h3>
              <p className="text-sm text-slate-600 text-center">
                Return to our homepage and explore our Wyoming IT services
              </p>
            </Link>

            <Link
              to="/services"
              className="flex flex-col items-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow group"
            >
              <svg className="w-12 h-12 text-blue-600 mb-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-semibold text-slate-900 mb-2">Our Services</h3>
              <p className="text-sm text-slate-600 text-center">
                Discover our cybersecurity and IT solutions for Wyoming businesses
              </p>
            </Link>

            <Link
              to="/contact"
              className="flex flex-col items-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow group"
            >
              <PhoneIcon className="w-12 h-12 text-blue-600 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-slate-900 mb-2">Contact Us</h3>
              <p className="text-sm text-slate-600 text-center">
                Get in touch with our Cheyenne IT experts for assistance
              </p>
            </Link>
          </div>

          {/* Popular Pages */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h3 className="text-xl font-heading font-semibold text-slate-900 mb-6">
              Popular Pages
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/about" className="text-blue-600 hover:text-blue-800 transition-colors">
                About Us
              </Link>
              <Link to="/capabilities" className="text-blue-600 hover:text-blue-800 transition-colors">
                Capabilities
              </Link>
              <Link to="/hardware-sales" className="text-blue-600 hover:text-blue-800 transition-colors">
                Hardware Sales
              </Link>
              <Link to="/software-licensing" className="text-blue-600 hover:text-blue-800 transition-colors">
                Software Licensing
              </Link>
              <Link to="/team" className="text-blue-600 hover:text-blue-800 transition-colors">
                Our Team
              </Link>
              <Link to="/book" className="text-blue-600 hover:text-blue-800 transition-colors">
                Book Consultation
              </Link>
              <Link to="/privacy" className="text-blue-600 hover:text-blue-800 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-blue-600 hover:text-blue-800 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-slate-900 text-white rounded-lg p-8">
            <h3 className="text-xl font-heading font-semibold mb-6">
              Need Help? Contact TyrantShield Technologies
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center justify-center md:justify-start">
                <PhoneIcon className="w-5 h-5 text-blue-400 mr-3" />
                <a href="tel:+13073177090" className="hover:text-blue-400 transition-colors">
                  (307) 317-7090
                </a>
              </div>
              <div className="flex items-center justify-center md:justify-start">
                <EnvelopeIcon className="w-5 h-5 text-blue-400 mr-3" />
                <a href="mailto:contact@tyrantshield.com" className="hover:text-blue-400 transition-colors">
                  contact@tyrantshield.com
                </a>
              </div>
            </div>
            <p className="text-slate-300 text-sm mt-4 text-center md:text-left">
              Serving Cheyenne, Wyoming and surrounding areas with enterprise-grade IT solutions
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;